import {FC, createContext, useContext} from 'react'
import {WithChildren} from '../helpers'
import {fetchData} from '../../reusable'

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

export const SUPPORTED_LANGS = ['en', 'ru', 'uz']

// guards the once-per-session pull of the language from the vendor profile
export const LANG_SYNCED_KEY = 'languageSyncedFromProfile'

type Lang = 'en' | 'ru' | 'uz'

type Props = {
  selectedLang: Lang
  // true once the user picked a language from a switcher - such a choice is never
  // overwritten by the profile, it is pushed to the profile instead
  userSelected?: boolean
}
const initialState: Props = {
  selectedLang: 'uz',
}

function getConfig(): Props {
  const ls = localStorage.getItem(I18N_CONFIG_KEY)
  if (ls) {
    try {
      const parsed = JSON.parse(ls) as Props
      if (parsed && SUPPORTED_LANGS.includes(parsed.selectedLang)) {
        return parsed
      }
    } catch (er) {
      console.error(er)
    }
  }
  return initialState
}

// Currently persisted language, without any React context
export function getSelectedLang(): Lang {
  return getConfig().selectedLang
}

// Did the user ever pick a language explicitly on this browser?
export function isUserSelectedLang(): boolean {
  return getConfig().userSelected === true
}

// Persist the language locally, without reloading. Returns true when the language changed,
// i.e. when the caller needs to reload for the new language to take effect.
export function saveLanguage(lang: string, userSelected = false): boolean {
  if (!SUPPORTED_LANGS.includes(lang)) {
    return false
  }
  const current = getConfig()
  localStorage.setItem(
    I18N_CONFIG_KEY,
    JSON.stringify({
      selectedLang: lang,
      // once set, the flag stays set - it records that this browser has an explicit choice
      userSelected: userSelected || current.userSelected === true,
    })
  )
  return current.selectedLang !== lang
}

function getVendorId(): number | undefined {
  try {
    const vendor = sessionStorage.getItem('vendor')
    if (!vendor) {
      return undefined
    }
    return JSON.parse(vendor)?.vendorId
  } catch (er) {
    console.error(er)
    return undefined
  }
}

// Push the selected language to the vendor profile so it survives across devices/sessions.
// Resolves to true when the profile was updated (or when there is nobody logged in to update).
export async function syncLanguageToProfile(lang: string): Promise<boolean> {
  const vendorId = getVendorId()
  if (!vendorId) {
    return true
  }
  try {
    const response: any = await fetchData('public/changelanguage', {language: lang, vendorId})
    return response?.status == '200' || response?.success === true
  } catch (er) {
    console.error('changelanguage failed', er)
    return false
  }
}

// guards against a second click while the changelanguage request is in flight
let switching = false

// Side effect: user picked a language explicitly from a switcher.
// The request is completed BEFORE the page reloads, otherwise the reload aborts it.
export async function setLanguage(lang: string) {
  if (switching || !SUPPORTED_LANGS.includes(lang) || getSelectedLang() === lang) {
    return
  }
  switching = true

  // never let a hanging request block the switch - the profile is re-synced on next boot
  const synced = await Promise.race([
    syncLanguageToProfile(lang),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 8000)),
  ])

  saveLanguage(lang, true)
  if (synced) {
    // profile and browser agree, no need to read the profile language again this session
    sessionStorage.setItem(LANG_SYNCED_KEY, '1')
  } else {
    // let LanguageInit retry the push on the next load
    sessionStorage.removeItem(LANG_SYNCED_KEY)
  }
  window.location.reload()
}

const I18nContext = createContext<Props>(initialState)

const useLang = () => {
  return useContext(I18nContext).selectedLang
}

const MetronicI18nProvider: FC<WithChildren> = ({children}) => {
  const lang = getConfig()
  return <I18nContext.Provider value={lang}>{children}</I18nContext.Provider>
}

export {MetronicI18nProvider, useLang}
